import {createSelector} from '@reduxjs/toolkit';
import isEqual from 'react-fast-compare';
import {Forecast} from '../../utils';
import {resolveSelectedForecastModel} from '../forecastModel/resolveSelectedForecastModel';
import {CommonRootState} from '../types';
import {selectForecastModelOrder} from '../userPreference/selectors';

export const getUserLocation = (state: CommonRootState) =>
  state.location.userLocation;

export const getRawSelectedLocationHash = (state: CommonRootState) =>
  state.location.selectedLocation;

const getLocationList = (state: CommonRootState) => state.location.locationList;

export const getSelectedLocation = createSelector(
  getRawSelectedLocationHash,
  getLocationList,
  getUserLocation,
  (selectedLocationHash, locationList, userLocation) => {
    if (selectedLocationHash) {
      const selectedLocation = locationList.find(
        (l) => l.hash === selectedLocationHash,
      );
      if (selectedLocation) {
        return selectedLocation;
      }
      if (selectedLocationHash === 'userLocation' && userLocation) {
        return userLocation;
      }
    }
    if (userLocation) {
      return userLocation;
    }
    if (locationList.length > 0) {
      // If no location is selected, return the first location in the list
      return locationList[0];
    }
    return null;
  },
  {
    memoizeOptions: {
      resultEqualityCheck: isEqual,
    },
  },
);

const getFavoriteLocationHashs = (state: CommonRootState) =>
  state.location.favoriteLocationHashs;

export const getFavoriteLocations = createSelector(
  getLocationList,
  getFavoriteLocationHashs,
  (locationList, favoriteLocationHashs) => {
    // Create a map for quick location lookup
    const locationMap = new Map(
      locationList.map((location) => [location.hash, location]),
    );

    // Return locations in the same order as favoriteLocationHashs
    return favoriteLocationHashs
      .filter((hash) => hash !== 'userLocation')
      .map((hash) => locationMap.get(hash))
      .filter((location) => location !== undefined);
  },
);

export const getRecentLocations = createSelector(
  getLocationList,
  getFavoriteLocationHashs,
  (locationList, favoriteLocationHashs) => {
    return locationList.filter(
      (l) =>
        !favoriteLocationHashs.includes(l.hash) && l.hash !== 'userLocation',
    );
  },
);

export const getHash = (_: CommonRootState, hash: string) => hash;

const getRawForecastModels = (state: CommonRootState) => {
  return state.location.forecastModels;
};

export const getForecastModelsByLocationHash = createSelector(
  getRawForecastModels,
  selectForecastModelOrder,
  getHash,
  (forecastModels, savedOrder, hash) => {
    if (hash in forecastModels) {
      const list = Forecast.ForecastUtils.getForecastModelList(
        forecastModels[hash],
      ).getList();
      return Forecast.ForecastUtils.orderForecastModelsByPreference(
        list,
        savedOrder,
      );
    }
    return [];
  },
);

const getRawSelectedForecastModel = (state: CommonRootState) => {
  return state.location.selectedForecastModel;
};

export const getSelectedForecastModelByLocationHash = createSelector(
  getForecastModelsByLocationHash,
  getRawSelectedForecastModel,
  selectForecastModelOrder,
  getHash,
  resolveSelectedForecastModel,
);
